import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Facebook, Lock } from 'lucide-react';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/vehicules', label: 'Véhicules' },
    { href: '/services', label: 'Services' },
    { href: '/a-propos', label: 'À Propos' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#DC2626] text-white py-2 relative z-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline">32 Rue Marcellin Berthelot, 59243 Quarouble</span>
            <span className="sm:hidden text-xs">Quarouble (59)</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/share/1CBFASZRBu/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              data-testid="facebook-link"
            >
              <Facebook size={16} />
            </a>
            <a
              href="tel:+33613851946"
              className="flex items-center gap-2 font-semibold hover:opacity-80 transition-opacity"
              data-testid="phone-link-top"
            >
              <Phone size={16} />
              06 13 85 19 46
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation - Glass effect */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20'
          : 'bg-[#0A0A0A] border-b border-white/5'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="bg-[#DC2626] text-white font-extrabold text-lg px-3 py-1.5">
                MCA
              </div>
              <div>
                <div className="font-bold text-white text-base lg:text-lg tracking-tight">MCA CAR</div>
                <div className="text-[10px] text-zinc-500 hidden sm:block tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Garage & Vente Auto</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm tracking-wide transition-colors duration-200 ${
                    isActive(link.href) ? 'text-white font-semibold' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button + Admin */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="px-5 py-2.5 bg-[#DC2626] text-white text-sm font-semibold tracking-wide transition-all duration-200 hover:bg-[#B91C1C] hover:shadow-lg hover:shadow-red-600/20"
                data-testid="nav-devis-button"
              >
                Demander un devis
              </Link>
              <Link
                to="/admin"
                className="flex items-center gap-1.5 px-3 py-2 text-xs text-zinc-600 hover:text-zinc-300 transition-colors"
                data-testid="nav-admin-button"
              >
                <Lock size={12} />
                Admin
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/5" style={{ background: 'var(--bg-primary)' }}>
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block py-3 px-4 text-sm ${
                    isActive(link.href) ? 'text-white bg-white/5 font-semibold' : 'text-zinc-400'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 space-y-3">
                <Link
                  to="/contact"
                  className="block text-center py-3 bg-[#DC2626] text-white font-semibold text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Demander un devis
                </Link>
                <a
                  href="tel:+33613851946"
                  className="flex items-center justify-center gap-2 py-3 text-[#DC2626] font-semibold text-sm"
                >
                  <Phone size={16} />
                  06 13 85 19 46
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
